import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  background: white;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid var(--gray-200);
  padding: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-700);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 8px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--gray-200);
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
  
  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  label {
    font-size: 14px;
    color: var(--gray-700);
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-200);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$variant === 'primary' ? 'var(--primary-600)' : 'white'};
  color: ${props => props.$variant === 'primary' ? 'white' : 'var(--gray-700)'};
  border: ${props => props.$variant === 'primary' ? 'none' : '1px solid var(--gray-300)'};
  
  &:hover {
    background: ${props => props.$variant === 'primary' ? 'var(--primary-700)' : 'var(--gray-50)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.span`
  font-size: 12px;
  color: var(--gray-500);
`;

const REGIONS = [
  'Africa',
  'Americas',
  'Asia',
  'Europe',
  'Oceania',
];

const CountryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    code3: '',
    name: '',
    native_name: '',
    region: '',
    subregion: '',
    currency_code: '',
    phone_code: '',
    flag_emoji: '',
    is_active: true,
  });

  useEffect(() => {
    if (isEdit) {
      fetchCountry();
    }
  }, [id]);

  const fetchCountry = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/countries/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching country:', error);
      toast.error('Failed to load country');
      navigate('/countries');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        code3: formData.code3?.toUpperCase() || null,
        currency_code: formData.currency_code?.toUpperCase() || null,
      };
      
      if (isEdit) {
        await api.put(`/countries/${id}`, payload);
        toast.success('Country updated successfully');
      } else {
        await api.post('/countries/', payload);
        toast.success('Country created successfully');
      }
      
      navigate('/countries');
    } catch (error) {
      console.error('Error saving country:', error);
      toast.error(error.response?.data?.error || 'Failed to save country');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <p>Loading...</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/countries')}>
          <FiArrowLeft />
        </BackButton>
        <Title>{isEdit ? 'Edit Country' : 'Add Country'}</Title>
      </PageHeader>

      <Card>
        <Form onSubmit={handleSubmit}>
          <SectionTitle>Basic Information</SectionTitle>
          
          <FormRow $cols={3}>
            <FormGroup>
              <Label>Country Code (2-letter) *</Label>
              <Input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="US"
                maxLength={2}
                required
              />
              <HelpText>ISO 3166-1 alpha-2</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label>Country Code (3-letter)</Label>
              <Input
                type="text"
                name="code3"
                value={formData.code3 || ''}
                onChange={handleChange}
                placeholder="USA"
                maxLength={3}
              />
              <HelpText>ISO 3166-1 alpha-3</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label>Flag Emoji</Label>
              <Input
                type="text"
                name="flag_emoji"
                value={formData.flag_emoji || ''}
                onChange={handleChange}
                placeholder="🇺🇸"
                maxLength={10}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Country Name *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="United States"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Native Name</Label>
              <Input
                type="text"
                name="native_name"
                value={formData.native_name || ''}
                onChange={handleChange}
                placeholder="United States"
              />
              <HelpText>Name in native language</HelpText>
            </FormGroup>
          </FormRow>

          <SectionTitle>Region</SectionTitle>

          <FormRow>
            <FormGroup>
              <Label>Region</Label>
              <Select
                name="region"
                value={formData.region || ''}
                onChange={handleChange}
              >
                <option value="">Select Region</option>
                {REGIONS.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Subregion</Label>
              <Input
                type="text"
                name="subregion"
                value={formData.subregion || ''}
                onChange={handleChange}
                placeholder="Northern America"
              />
            </FormGroup>
          </FormRow>

          <SectionTitle>Currency & Phone</SectionTitle>

          <FormRow>
            <FormGroup>
              <Label>Default Currency Code</Label>
              <Input
                type="text"
                name="currency_code"
                value={formData.currency_code || ''}
                onChange={handleChange}
                placeholder="USD"
                maxLength={3}
              />
              <HelpText>ISO 4217 currency code</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label>Phone Code</Label>
              <Input
                type="text"
                name="phone_code"
                value={formData.phone_code || ''}
                onChange={handleChange}
                placeholder="+1"
              />
            </FormGroup>
          </FormRow>

          <FormRow $cols={1}>
            <Checkbox>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <label htmlFor="is_active">Active</label>
            </Checkbox>
          </FormRow>

          <ButtonGroup>
            <Button type="button" onClick={() => navigate('/countries')}>
              Cancel
            </Button>
            <Button type="submit" $variant="primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Country'}
            </Button>
          </ButtonGroup>
        </Form>
      </Card>
    </PageContainer>
  );
};

export default CountryForm;
